/**
 * InternalConfirmAppointment renders the admin-only booking confirmation step.
 * Staff can review the appointment summary, tweak client details/notes, and
 * atomically write the appointment to Firestore plus employee schedules.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Grid2, Stack, TextField, Typography, Avatar, Autocomplete, Card, CardContent, Divider } from '@mui/material';
import { AccessTime, DateRange, Person } from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { Appointment, AppointmentStatus, BookingConfirmation, Customer, Employee, INTERNAL_APPOINTMENT_TAG, ScheduleCacheUpdateInput, Service } from '../../types';
import { formatDate, formatTimeRange, isAppointmentEndTimePast } from '../../time/timeUtils';
import { addBookingToSchedule, addToEmployeeAppointments, createAppointment, deleteAppointment, fetchCustomersPage, getImageFromStorage } from '../../firebase/firebase-delegate';
import { getCachedAssetUrl } from '../../utils/assetCache';
import { InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useTenant } from '../../tenancy/TenantContext';
import { appointmentPriceIsPending, formatBookingTotal } from '../../utils/servicePrice';
import ServiceQuestionsFields, {
  answerDraftsFrom, ServiceAnswerDrafts, serviceAnswersFrom, servicesWithQuestions,
} from './ServiceQuestionsFields';
import { getTenantPublicOrigin } from '../../sitePublishing/publicConfigSource';

interface InternalConfirmAppointmentProps {
  employee: Employee;
  appointment: Appointment;
  businessName: string;
  enabledEmployeeSMS: boolean;
  handleAppointment: (appointment: Appointment | null) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  handleErrorDialogOpen: (message: string, options?: { refreshSchedule?: boolean }) => void;
  handleScheduleCacheUpdate: (input: ScheduleCacheUpdateInput) => void;
  handleBookingConfirmation: (bookingDetails: BookingConfirmation, attemptedCardPayment: boolean, paid: boolean) => void;
  goToPreviousStep: () => void;
  clearAppointmentOnSuccess?: boolean;
  // The selected services, for their customer questions (optional for staff).
  selectedServices?: Service[] | null;
}

const InternalConfirmAppointment: React.FC<InternalConfirmAppointmentProps> = ({
  employee,
  appointment,
  businessName,
  enabledEmployeeSMS,
  handleAppointment,
  setIsSubmitting,
  handleErrorDialogOpen,
  handleScheduleCacheUpdate,
  handleBookingConfirmation,
  goToPreviousStep,
  clearAppointmentOnSuccess = false,
  selectedServices,
}) => {
  const questionServices = servicesWithQuestions(selectedServices);
  const [answerDrafts, setAnswerDrafts] = useState<ServiceAnswerDrafts>(
    () => answerDraftsFrom(questionServices, appointment.serviceAnswers));
  const { tenantId } = useTenant();
  const tenantPublicOriginQuery = useQuery({
    queryKey: ['tenantPublicOrigin', tenantId],
    queryFn: () => getTenantPublicOrigin(tenantId),
    staleTime: 60_000,
  });
  // Local editing state mirrors the appointment draft while the admin confirms.
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [notes, setNotes] = useState(appointment.notes || '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [employeePhotoUrl, setEmployeePhotoUrl] = useState<string | null>(null);

  // Prefill client info with any customer info that already exists on the draft.
  useEffect(() => {
    const defaultName = [appointment.customerFirstName, appointment.customerLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    setClientName(defaultName);
    setEmail(appointment.customerEmail || '');
    setPhone(appointment.customerPhoneNumber || '');
  }, [
    appointment.customerFirstName,
    appointment.customerLastName,
    appointment.customerEmail,
    appointment.customerPhoneNumber,
  ]);

  // Keep notes synced when the draft changes upstream (e.g., going back a step).
  useEffect(() => {
    setNotes(appointment.notes || '');
  }, [appointment.notes]);

  // Load employee profile picture
  useEffect(() => {
    let isMounted = true;
    const loadEmployeePhoto = async () => {
      const profilePath = employee.profilePictureImagePath;
      if (!profilePath) {
        setEmployeePhotoUrl(null);
        return;
      }

      try {
        const url = await getCachedAssetUrl(
          profilePath,
          () => getImageFromStorage(profilePath)
        );
        if (isMounted) {
          setEmployeePhotoUrl(url);
        }
      } catch (error) {
        console.error('Failed to load employee profile picture', error);
        if (isMounted) {
          setEmployeePhotoUrl(null);
        }
      }
    };

    loadEmployeePhoto();
    return () => {
      isMounted = false;
    };
  }, [employee.profilePictureImagePath]);

  // Computed summary keeps the JSX tidy and avoids repeated formatting calls.
  const appointmentSummary = useMemo(() => ({
    title: appointment.appointmentTitle,
    date: formatDate(appointment.appointmentDate),
    timeRange: formatTimeRange(appointment.appointmentTime, appointment.appointmentDuration),
    total: appointment.billingAmount,
  }), [appointment]);

  type CustomerPage = {
    customers: Customer[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
  };

  const customersQuery = useInfiniteQuery<
    CustomerPage,
    Error,
    InfiniteData<CustomerPage>,
    ['internalCustomers', string, string, boolean],
    QueryDocumentSnapshot<DocumentData> | null
  >({
    queryKey: ['internalCustomers', tenantId, employee.id, employee.admin],
    queryFn: ({ pageParam }) =>
      fetchCustomersPage({
        siteId: tenantId,
        employeeId: employee.admin ? null : employee.id,
        pageSize: 50,
        cursor: pageParam ?? null,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc ?? undefined : undefined),
    initialPageParam: null,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const customers = useMemo(
    () => customersQuery.data?.pages.flatMap((page) => page.customers) ?? [],
    [customersQuery.data]
  );

  const filteredCustomers = useMemo(() => {
    const term = clientName.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) => {
      const first = (customer.firstName ?? '').toLowerCase();
      const last = (customer.lastName ?? '').toLowerCase();
      const full = `${first} ${last}`.trim();
      return first.includes(term) || last.includes(term) || full.includes(term);
    });
  }, [customers, clientName]);

  type ClientOption =
    | { type: 'add'; label: string }
    | { type: 'customer'; customer: Customer; label: string };

  const clientOptions: ClientOption[] = useMemo(() => {
    const term = clientName.trim();
    const base = filteredCustomers.map((customer) => ({
      type: 'customer' as const,
      customer,
      label: `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || 'Unnamed',
    }));
    if (!term) return base;
    return [{ type: 'add', label: '+ Add New Client' }, ...base];
  }, [filteredCustomers, clientName]);

  const formatPhoneLabel = (value?: string | null) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return value;
  };

  // Propagate note edits to the parent appointment draft.
  const handleNotesChange = (value: string) => {
    setNotes(value);
    handleAppointment({ ...appointment, notes: value });
  };

  // Track client name changes locally so we can run validation messaging.
  const handleClientNameChange = (value: string) => {
    setClientName(value);
    if (selectedCustomer) {
      setSelectedCustomer(null);
      setShowMoreInfo(false);
      setEmail('');
      setPhone('');
    }
    if (localError) {
      setLocalError(null);
    }
  };

  const handleSelectCustomer = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      const fullName = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
      setClientName(fullName);
      setEmail(customer.email || '');
      setPhone(customer.phone || '');
      setShowMoreInfo(false);
    }
  };

  // Construct the Firestore appointment payload in the shape other delegates expect.
  const buildAppointmentPayload = (firstName: string, lastName: string) => {
    const appointmentHasEnded = isAppointmentEndTimePast(appointment);
    const status = appointmentHasEnded ? AppointmentStatus.COMPLETE : AppointmentStatus.INTERNAL_BOOKING;

    const tags = Array.from(new Set([...(appointment.tags ?? []), INTERNAL_APPOINTMENT_TAG]));

    return {
      employeeID: employee.id,
      employeeName: `${employee.firstName} ${employee.lastName}`,
      employeeEmail: employee.email || '',
      employeePhoneNumber: employee.phone || '',
      billingAmount: appointment.billingAmount,
      creationTime: Timestamp.now(),
      appointmentDate: appointment.appointmentDate,
      appointmentTime: appointment.appointmentTime,
      status,
      customerEmail: email.trim(),
      customerFirstName: firstName,
      customerLastName: lastName,
      customerPhoneNumber: phone.trim(),
      appointmentTitle: appointment.appointmentTitle,
      appointmentDuration: appointment.appointmentDuration,
      servicesList: appointment.servicesList,
      businessName,
      businessUrl: tenantPublicOriginQuery.data ?? '',
      lastUpdater: employee.id,
      allowSendCustomerSMS: false,
      allowSendEmployeeSMS: enabledEmployeeSMS,
      tags,
      ...(notes.trim() ? { notes: notes.trim().slice(0, 160) } : {}),
      ...(appointmentPriceIsPending(appointment) ? { priceToConfirm: true } : {}),
      ...(serviceAnswersFrom(questionServices, answerDrafts).length ?
        { serviceAnswers: serviceAnswersFrom(questionServices, answerDrafts) } : {}),
    };
  };

  const handleSubmit = async () => {
    // Validate that the draft has the essentials before we write anything.
    if (!appointment.appointmentDate || !appointment.appointmentTime || appointment.appointmentDuration <= 0) {
      setLocalError('Pick a valid date & time.');
      return;
    }

    if (!appointment.servicesList.length) {
      setLocalError('Select at least one service.');
      return;
    }

    const trimmedName = clientName.trim();
    if (!trimmedName) {
      setLocalError('Client name is required.');
      return;
    }

    let trimmedFirstName = '';
    let trimmedLastName = '';
    if (selectedCustomer) {
      trimmedFirstName = selectedCustomer.firstName || '';
      trimmedLastName = selectedCustomer.lastName || '';
    } else {
      const [first, ...rest] = trimmedName.split(/\s+/);
      trimmedFirstName = first;
      trimmedLastName = rest.join(' ');
    }

    setLocalError(null);
    setIsSubmitting(true);

    const payloadWithoutId = buildAppointmentPayload(trimmedFirstName, trimmedLastName || ' ');
    let appointmentId: string | null = null;
    let didUpdateSchedule = false;
    let tempCompleteAppointment: Appointment | null = null;

    try {
      // Reserve the employee's schedule slot first so no overlapping bookings sneak in.
      const scheduleUpdated = await addBookingToSchedule(
        employee.id,
        appointment.appointmentDate,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        false,
        undefined,
        tenantId
      );
      if (!scheduleUpdated) {
        throw new Error('Failed to update employee schedule. The selected slot may already be booked.');
      }
      didUpdateSchedule = true;

      appointmentId = await createAppointment(payloadWithoutId, tenantId);
      tempCompleteAppointment = { ...payloadWithoutId, id: appointmentId } as Appointment;

      // Link the appointment to the employee's collection for quick lookups.
      await addToEmployeeAppointments(employee.id, appointmentId, appointment.appointmentDate, tenantId);

      if (!tempCompleteAppointment) {
        throw new Error('Appointment not finalized. Please try again.');
      }

      handleBookingConfirmation({ ...tempCompleteAppointment, employee: `${employee.firstName} ${employee.lastName}` }, false, false);

      handleScheduleCacheUpdate({
        employeeId: employee.id,
        appointmentDate: appointment.appointmentDate,
        appointmentTime: appointment.appointmentTime,
        appointmentDuration: appointment.appointmentDuration,
      });

      if (clearAppointmentOnSuccess) {
        handleAppointment(null);
      }
    } catch (error: any) {
      const message = error?.message || 'Failed to book appointment. Please try again.';

      if (tempCompleteAppointment) {
        try {
          await deleteAppointment(tempCompleteAppointment, tenantId);
        } catch (cleanupError) {
          console.error('Failed to delete internal appointment after error', cleanupError);
        }
      } else if (didUpdateSchedule) {
        try {
          // If we never stored the appointment, free up the previously reserved slot.
          await addBookingToSchedule(
            employee.id,
            appointment.appointmentDate,
            appointment.appointmentTime,
            appointment.appointmentDuration,
            true,
            undefined,
            tenantId
          );
        } catch (cleanupError) {
          console.error('Failed to revert schedule after internal booking error', cleanupError);
        }
      }

      handleErrorDialogOpen(message, { refreshSchedule: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid2 container spacing={2} direction="column" alignItems="center" sx={{ minHeight: '100%' }}>
      <Container
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          maxWidth: 800,
        }}
      >
        <Box sx={{ flexGrow: 1, margin: '0 auto', px: { xs: 0, sm: 2 }, width: '100%' }}>
          <Box sx={{ height: 8 }} />
          <Card elevation={2} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
                  Appointment Details:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {appointmentSummary.title}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {employeePhotoUrl ? (
                    <Avatar
                      src={employeePhotoUrl}
                      alt={`${employee.firstName} ${employee.lastName}`}
                      sx={{ width: 36, height: 36 }}
                    >
                      {employee.firstName?.charAt(0).toUpperCase() ?? '?'}
                    </Avatar>
                  ) : (
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}>
                      <Person sx={{ color: 'text.secondary' }} />
                    </Avatar>
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {employee.firstName} {employee.lastName}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <DateRange sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {appointmentSummary.date}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AccessTime sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {appointmentSummary.timeRange}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total
                  </Typography>
                  <Stack alignItems="flex-end" spacing={0.25}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {formatBookingTotal(appointment)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Pay in-person
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={3} sx={{ mt: 3 }}>
            <Card elevation={2} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Enter Your Info:
                  </Typography>
                  <Autocomplete
                    options={clientOptions}
                    value={
                      selectedCustomer
                        ? {
                          type: 'customer',
                          customer: selectedCustomer,
                          label: `${selectedCustomer.firstName ?? ''} ${selectedCustomer.lastName ?? ''}`.trim() || 'Unnamed',
                        }
                        : null
                    }
                    onChange={(_, value) => {
                      if (!value) {
                        return;
                      }
                      if (value.type === 'add') {
                        setSelectedCustomer(null);
                        setShowMoreInfo(false);
                        setEmail('');
                        setPhone('');
                        return;
                      }
                      handleSelectCustomer(value.customer);
                    }}
                    inputValue={clientName}
                    onInputChange={(_, value, reason) => {
                      if (reason !== 'input' && reason !== 'clear') {
                        return;
                      }
                      if (value === '+ Add New Client' || value === 'Add New Client') {
                        return;
                      }
                      handleClientNameChange(value);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.type === 'customer' && value.type === 'customer'
                        ? option.customer.id === value.customer.id
                        : option.type === value.type
                    }
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option) => (
                      <li {...props} key={option.type === 'customer' ? option.customer.id : option.type}>
                        {option.type === 'add' ? (
                          <Typography variant="body1" fontWeight={600}>
                            {option.label}
                          </Typography>
                        ) : (
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body1">{option.label || 'Unnamed'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatPhoneLabel(option.customer.phone)}
                            </Typography>
                          </Box>
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client name"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                  {customersQuery.hasNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button
                        variant="text"
                        onClick={() => customersQuery.fetchNextPage()}
                        disabled={customersQuery.isFetchingNextPage}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
                      >
                        {customersQuery.isFetchingNextPage ? 'Loading…' : 'Load more clients'}
                      </Button>
                    </Box>
                  )}
                  {!selectedCustomer && clientName.trim() && (
                    <Button
                      variant="text"
                      onClick={() => setShowMoreInfo((prev) => !prev)}
                      sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 0, py: 0, minHeight: 0 }}
                    >
                      + Add More Info
                    </Button>
                  )}
                  {selectedCustomer && (
                    <Button
                      variant="text"
                      onClick={() => setShowMoreInfo((prev) => !prev)}
                      sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 0, py: 0, minHeight: 0 }}
                    >
                      + Show More Info
                    </Button>
                  )}

                  {showMoreInfo && (
                    <Stack spacing={1.5}>
                      <style>{`
                        .PhoneInputInput {
                          height: 40px !important;
                          padding: 10.5px 14px !important;
                          font-size: 16px !important;
                          border-radius: 8px !important;
                          border: 1px solid rgba(0, 0, 0, 0.23) !important;
                          box-sizing: border-box !important;
                          background: #fff !important;
                        }
                      `}</style>
                      <PhoneInput
                        defaultCountry="US"
                        international
                        value={phone}
                        onChange={(value) => setPhone(value || '')}
                        className="PhoneInput"
                        disabled={!!selectedCustomer}
                      />
                      <TextField
                        label={selectedCustomer ? "Email" : "Email (optional)"}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        fullWidth
                        size="small"
                        disabled={!!selectedCustomer}
                      />
                    </Stack>
                  )}

                  <ServiceQuestionsFields services={questionServices} drafts={answerDrafts} allRequiredOptional
                    onChange={setAnswerDrafts} />
                  <TextField
                    label="Notes (optional)"
                    size="small"
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(event) => handleNotesChange(event.target.value)}
                    fullWidth
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          {localError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {localError}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            zIndex: 2,
          }}
        >
          <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="text"
              onClick={goToPreviousStep}
              sx={{ width: '100%', borderRadius: '4px', fontWeight: '600', fontSize: '1rem', py: 1.2 }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={
                appointment.appointmentDuration <= 0 ||
                !appointment.appointmentDate ||
                !appointment.appointmentTime ||
                appointment.servicesList.length === 0
              }
              sx={{ width: '100%', borderRadius: '4px', fontWeight: '600', fontSize: '1rem', py: 1.2 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Container>
    </Grid2>
  );
};

export default InternalConfirmAppointment;
